import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import PageTitle from '../../headers/PageTitle';
import { NotificationSendsTable } from '../../MapPage/CorporateViewTabs/NotificationsTab/NotificationSendsTable';
import {
  filterSends,
  SENDS_TAB_CHOICES,
  SendsTab,
  tabCounts,
  type NotificationSendDto,
} from '../../MapPage/CorporateViewTabs/NotificationsTab/notificationSendsModel';
import {
  useCancelNotification,
  useOrgNotificationSends,
} from 'hooks/useNotification';

const EMPTY_MESSAGE: Record<SendsTab, string> = {
  [SendsTab.SENT]: 'Nothing has been sent from this organization yet.',
  [SendsTab.SCHEDULED]: 'No sends are waiting to go out.',
  [SendsTab.DRAFTS]: 'No drafts.',
  [SendsTab.FAILED]: 'Nothing has failed, which is the point.',
};

/**
 * Every notification this organization has sent, whoever sent it.
 *
 * The bell and the composer's own list are both scoped to the signed-in user,
 * so this is the only place a duty officer can see what went out overnight
 * without asking the person who sent it.
 */
const NotificationSendsPage: React.FC = () => {
  const navigate = useNavigate();
  const { data, isLoading, isError } = useOrgNotificationSends();
  const cancelSend = useCancelNotification();
  const [tab, setTab] = useState<SendsTab>(SendsTab.SENT);
  const [search, setSearch] = useState('');

  const sends = useMemo(() => data ?? [], [data]);
  const counts = useMemo(() => tabCounts(sends), [sends]);
  const visible = useMemo(
    () => filterSends(sends, tab, search),
    [sends, tab, search]
  );

  const emptyMessage = search.trim()
    ? `No sends match “${search}”.`
    : EMPTY_MESSAGE[tab];

  return (
    <div className="p-4 pt-0">
      <PageTitle title="Notification Sends" />
      <p className="text-muted mb-4">
        Every notification sent from this organization, with who it reached, who
        answered, and how far any escalation got.
      </p>

      {isError ? (
        <p className="text-danger">
          The send history could not be loaded. You may not have permission to
          see other people&apos;s sends.
        </p>
      ) : (
        <Tabs
          value={tab}
          onValueChange={(next) => setTab(next as SendsTab)}
          className="tw:w-full"
        >
          <TabsList variant="line" className="tw:w-full tw:justify-start">
            {SENDS_TAB_CHOICES.map((choice) => (
              <TabsTrigger key={choice.value} value={choice.value}>
                {choice.label}
                {counts[choice.value] > 0 ? ` (${counts[choice.value]})` : ''}
              </TabsTrigger>
            ))}
          </TabsList>

          {SENDS_TAB_CHOICES.map((choice) => (
            <TabsContent
              key={choice.value}
              value={choice.value}
              className="tw:mt-3"
            >
              <NotificationSendsTable
                sends={visible}
                search={search}
                onSearchChange={setSearch}
                onOpenSend={(send: NotificationSendDto) =>
                  navigate(`/notifications/sends/${send.id}`)
                }
                onCancelSend={(send: NotificationSendDto) =>
                  void cancelSend.mutateAsync({ notificationId: send.id })
                }
                emptyMessage={isLoading ? 'Loading sends…' : emptyMessage}
              />
            </TabsContent>
          ))}
        </Tabs>
      )}
    </div>
  );
};

export default NotificationSendsPage;
